import { flexRender, Table, Row } from '@tanstack/react-table';
import React, { useRef, useEffect } from 'react';
import { useWindowDimensions, useAppSelector, useAppDispatch } from 'src/hooks';
import '../../pages/analytics/simulator/pricing-simulator/components/retail-price-simulation-table/retail-price-simulation.scss';
import { useVirtual } from 'react-virtual';
import GetExternalHeadersRowComponent from 'src/pages/analytics/simulator/common/features/table/components/external-headers-row-component';
import { setExternalColumnsNumber } from 'src/pages/analytics/simulator/pricing-simulator/store/pricing-simulator-slice';
import { handlePpgHighlightedData } from 'src/pages/analytics/simulator/common/features/table/store/table-slice';
import { round2 } from 'src/pages/analytics/simulator/common/features/table/utils/table-utils';
/**
 * Function to generate a dictionary with column headers as keys and their widths as values
 * @param table The table element
 * @returns A dictionary where keys are column headers and values are column widths
 */
function getColumnWidths(table: HTMLTableElement): { [key: string]: number } {
  const columnWidths: { [key: string]: number } = {};
  const headerRows = table.querySelectorAll('thead');
  
  headerRows.forEach((headerRow) => {
    const headerCells = headerRow.querySelectorAll('th');
    
    headerCells.forEach((headerCell, index) => {
      const headerText = headerCell.textContent?.trim() || '';
      const columnWidth = headerCell.clientWidth;
      if (headerText) {
        columnWidths[headerText] = columnWidth;
      }
    });
  });

  return columnWidths;
}

/**
 * Function to calculate the width for each external row column based on the table column widths.
 * @param columnWidths The dictionary of column names and their corresponding widths.
 * @param externalColumnMapping The mapping between external row column names and table columns.
 * @returns An object where the keys are the external row column names and values are their calculated widths.
 */

function calculateExternalRowWidths(
  columnWidths: { [key: string]: number },
  externalColumnMapping: { [externalColumn: string]: string[] }
) {
  const externalRowWidths: { [key: string]: number } = {};
  // Loop through each external row column and sum up the widths of the mapped table columns
  for (const externalColumn in externalColumnMapping) {
    if (externalColumnMapping.hasOwnProperty(externalColumn) && externalColumn != 'legendflag') {
      const tableColumns = externalColumnMapping[externalColumn];
      const totalWidth = tableColumns.reduce((sum, tableColumn) => {
        return sum + (columnWidths[tableColumn] || 0); // Sum the widths of each table column mapped
      }, 0);
      
      externalRowWidths[externalColumn] = totalWidth - 8.5;
    }
  }
  externalRowWidths['legendflag']=1 //to add the legend flag dummy column in external row, next to PPG

  return externalRowWidths;
}

export function makeColumnsSticky(
  table: HTMLTableElement,
  cogsVisible?: boolean
) {
  let totalWidth = 0;
  // getting td elements
  const tdElements: NodeListOf<HTMLTableCellElement> = table.querySelectorAll(
    'td.simulation-results'
  );
  // getting th elements
  const thElements = table.querySelector('tr.secondary-header-row');

  tdElements.forEach((element) => {
    const parentRow: HTMLTableRowElement =
      element.parentElement as HTMLTableRowElement;
    parentRow.childNodes.forEach((node, index, self) => {
      const cell = node as HTMLTableCellElement;
      const isLastCell: any = index === thElements.childNodes.length - 1;
      if (cell.classList.contains('simulation-results')) {
        const cellWidth = cell.clientWidth + 1;
        cell.setAttribute(
          'style',
          cogsVisible === true
            ? `right: ${ (self.length - (index + 1)) * cellWidth - (isLastCell ? 0 : 4) }px`
            : `right: ${(self.length - (index + 1)) * cellWidth}px`
        );
      }
    });
  });

  if (thElements) {
    thElements.childNodes.forEach((node, index, self) => {
      const cell = node as HTMLTableCellElement;
      const isLastCell: any = index === thElements.childNodes.length - 1;

      if (cell.classList.contains('simulation-results')) {
        const cellWidth = cell.clientWidth + 1;
        totalWidth += cellWidth;

        cell.setAttribute(
          'style',
          cogsVisible === true
            ? `right: ${(self.length - (index + 1)) * cellWidth - (isLastCell ? 0 : 4)}px`
            : `right: ${(self.length - (index + 1)) * cellWidth}px`
        );
      }
    });
  }

  const tfootElement = table.querySelector('tfoot tr');
  if (tfootElement) {
    tfootElement.childNodes.forEach((node, index, self) => {
      const cell = node as HTMLTableCellElement;
      if (cell.classList.contains('simulation-results')) {
        const remainingCells = [];
        for (
          let currentIndex = index + 1;
          currentIndex < self.length;
          currentIndex++
        ) {
          const nextCell = self[currentIndex];
          if (nextCell) {
            remainingCells.push(nextCell);
          }
        }
        const right = remainingCells
          .map((nextCell) => (nextCell as HTMLTableCellElement).clientWidth + 1)
          .reduce(
            (previousValue, currentValue) => previousValue + currentValue,
            0
          );
        cell.setAttribute('style', `right: ${right}px`);
      }
    });
  }

  return totalWidth;
}

interface ITableProps {
  table: Table<any>;
  setHeightExplicitly: boolean;
  tableRef: React.MutableRefObject<HTMLTableElement>;
  extClassName: string;
  showFooterLabel?: boolean;
  isRowSelect?: boolean;
  isMultiple?: boolean;
  externalColumnData?: any;
  visibleColumns?: any;
  activeHeader?: any;
}

export default function TableComponent({
  table,
  tableRef,
  setHeightExplicitly = true,
  extClassName = '',
  showFooterLabel = true,
  isRowSelect = false,
  isMultiple = false,
  externalColumnData=[],
  visibleColumns,
  activeHeader
}: ITableProps) {
  const { height } = useWindowDimensions();
  const tableContainer = useRef<HTMLDivElement>();
  const { cogsDialogVisible } = useAppSelector((state) => state.plannerV2);
  const {
    priceThresholdData: {
      is_price_threshold_eligible = {},
      child_field_ppg = ''
    }, ppgHighlightedData
  } = useAppSelector((state) => state.table);
  const { columnsMapping, kpiSelection } = useAppSelector((state) => state.pricingSimulator);

  const currentModule = window.location.href.split('/').slice(-1)[0];
  const appDispatch = useAppDispatch();
  const ppg_highlighted = useRef<{ [ppg: string]: Set<string> }>({});

  useEffect(() => {
    if (tableContainer && tableContainer.current && setHeightExplicitly) {
      tableContainer.current.style.height = `${
        height - (tableContainer.current.getBoundingClientRect().top + 50)
      }px`;
    }
  });

  useEffect(() => {
    const clone: { [ppg: string]: Set<string> } = {};
    if (ppgHighlightedData) {
      for (const key in ppgHighlightedData) {
        clone[key] = new Set(ppgHighlightedData[key]);
      }
    }
    ppg_highlighted.current = clone;
  
    return () => {
      if (Object.keys(ppg_highlighted.current).length > 0) {
        appDispatch(handlePpgHighlightedData(ppg_highlighted.current));
        ppg_highlighted.current = {};
      }
    };
  }, []);

  const { rows } = table.getRowModel();
  const rowVirtualizer = useVirtual({
    parentRef: tableContainer,
    size: rows.length,
    overscan: 10
  });

  const { virtualItems: virtualRows, totalSize } = rowVirtualizer;
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  const onRowSelect = (row: Row<any>) => {
    if (isRowSelect) {
      if (!isMultiple) {
        table.toggleAllRowsSelected(false);
      }
      row.toggleSelected(!row.getIsSelected());
    }
  };
  
  const checkPriceThreshold = (row: Row<any>, cell: any) => {
    if (kpiSelection !== 'Retailer Metrics') return '';

    const price_threshold_flag = round2(row?.original?.price_threshold_flag);
    const price_threshold_flag_1 = round2(row?.original?.price_threshold_flag_1);
    const price_threshold_flag_2 = round2(row?.original?.price_threshold_flag_2);
    let is_threshold_limit_crossed = false;
    is_threshold_limit_crossed = (price_threshold_flag < 0 || price_threshold_flag_1 < 0 || price_threshold_flag_2 < 0) ? true : false;
    const accessorKey = cell?.column?.columnDef?.accessorKey || cell?.column?.columnDef?.id;
    const column_flag = is_price_threshold_eligible[accessorKey] || false;
    const child_field = row?.original?.[child_field_ppg] || 'NAN';
    const child_field_id = row?.original?.index;
    const ppg = row?.original?.ppg || '';
  if (!ppg_highlighted.current[ppg]) {
    ppg_highlighted.current[ppg] = new Set<string>();
  }

  const highlightedSet = ppg_highlighted.current[ppg];
  // Function to recursively check subRows
  const checkSubRows = (subRows: Row<any>[]): boolean => {
    return subRows.some((subRow) => {
      const subRowPriceThresholdFlag = round2(subRow?.original?.price_threshold_flag);
      const subRowPriceThresholdFlag1 = round2(subRow?.original?.price_threshold_flag_1);
      const subRowPriceThresholdFlag2 = round2(subRow?.original?.price_threshold_flag_2);
      const subRowIsThresholdLimitCrossed = subRowPriceThresholdFlag < 0 || subRowPriceThresholdFlag1 < 0 || subRowPriceThresholdFlag2 < 0;
      const accessorKey = cell?.column?.columnDef?.accessorKey || cell?.column?.columnDef?.id;
      const column_flag = is_price_threshold_eligible[accessorKey] || false;
      if (subRowIsThresholdLimitCrossed && column_flag) {
        return true;
      }
      if (subRow.subRows && subRow.subRows.length > 0) {
        return checkSubRows(subRow.subRows);
      }
      return false;
    });
  };

  // Check if the current row is a parent and has subRows
  if (row.subRows && row.subRows.length > 0) {
    const hasSubRowThresholdCrossed = checkSubRows(row.subRows);
    if (hasSubRowThresholdCrossed) {
      return 'price-threshold-cell';
    }
  }
  // ✅ Parent row logic — highlight if any child fields are in the set
    if (child_field === 'NAN') {
    return ((highlightedSet.size > 0 || is_threshold_limit_crossed === true) && column_flag) ? 'price-threshold-cell' : '';
  }

  // ✅ Child row: Highlight and add to the set if it qualifies
  if (is_threshold_limit_crossed === true && column_flag) {
    highlightedSet.add(child_field_id);
    return 'price-threshold-cell';
  }

  // ✅ Child row: Remove from set if it doesn't qualify anymore
  if (column_flag && is_threshold_limit_crossed === false && highlightedSet.has(child_field_id)) {
    highlightedSet.delete(child_field_id);
    ppg_highlighted.current[ppg] = highlightedSet;
    return '';
  }

  return '';
  };

  let totalWidth;
  let columnWidths;
  let externalRowWidths;

  if (tableRef.current && tableContainer.current) {
    totalWidth = makeColumnsSticky(tableRef.current, cogsDialogVisible);
    columnWidths = getColumnWidths(tableRef.current); // Get column widths
    externalRowWidths = calculateExternalRowWidths(columnWidths, columnsMapping);
  }
  
  useEffect(() => {
      if (totalWidth) { 
        // appDispatch(setExternalColumnsNumber(externalRowWidths))
        appDispatch(setExternalColumnsNumber({ 'Sim Results': `${totalWidth}px` }));
        // totalWidth > 200 && appDispatch(setExternalColumnsNumber({ PPG: '400px' }));
      }
  }, [cogsDialogVisible, totalWidth]);

  return (
    <div
      ref={tableContainer}
      className={`virtualized-table-container ${extClassName}`}
      onScroll={() => {
        if (tableRef.current) {
          makeColumnsSticky(tableRef.current, cogsDialogVisible);
        }
      }}
    >
        {GetExternalHeadersRowComponent(externalColumnData, externalRowWidths, visibleColumns, activeHeader)}
      <table ref={tableRef}>
        <thead
          style={externalColumnData?.length > 0 ? { top: '26.8px' } : {}}

        >
          {table?.getHeaderGroups()?.map((headerGroup, rowIndex) => (
            <tr
              key={headerGroup.id} // Ensure headerGroup has a valid id
              className={rowIndex > 0 ? 'secondary-header-row' : ''}
            >
              {headerGroup.headers.map((header) => (
                <th
                  style={{
                    textAlign: 'center',
                    ...header.column.columnDef.meta?.headerStyle
                   }}
                  key={header.id} // Ensure header.id is unique
                  colSpan={header.colSpan}
                  className={header.column.columnDef.meta?.className}
                  {...(header.column.getCanSort()
                    ? { onClick: header.column.getToggleSortingHandler() }
                    : {})}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {paddingTop > 0 && (
            <tr>
              <td style={{ height: `${paddingTop}px` }} />
            </tr>
          )}
          {virtualRows.map((virtualRow) => {
            const row = rows[virtualRow.index] as Row<any>;
            return (
              <tr
                key={row.id || `row-${virtualRow.index}`}
                className={
                  row.getIsSelected()
                    ? currentModule.toLowerCase() === 'scenario-manager' &&
                      row.original.is_kpi_updated
                      ? 'selected-row cogs-updated-row'
                      : 'selected-row'
                    : currentModule.toLowerCase() === 'scenario-manager' &&
                      row.original.is_kpi_updated
                    ? 'cogs-updated-row'
                    : ''
                }
                onClick={() => onRowSelect(row)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    style={cell.column.columnDef.meta?.style}
                    className={`${checkPriceThreshold(row,cell)} ${cell.column.columnDef.meta?.className || ''}`}
                    key={cell.id}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
          {paddingBottom > 0 && (
            <tr>
              <td style={{ height: `${paddingBottom}px` }} />
            </tr>
          )}
        </tbody>
        <tfoot>
          {table
            .getFooterGroups()
            .filter((_, index) => index === 0)
            .map((footerGroup) => (
              <tr key={footerGroup.id}>
                {footerGroup.headers.map((header, index, self) => {
                  const nextItem = self[index + 1];
                  if (
                    !header.column.columnDef.meta?.className?.includes(
                      'simulation-results'
                    ) &&
                    nextItem &&
                    nextItem.column.columnDef.meta?.className?.includes(
                      'simulation-results'
                    ) &&
                    showFooterLabel
                  ) {
                    return (
                      <th key={header.id} className='simulation-results'>
                        Aggregated Result
                      </th>
                    );
                  }
                  return (
                    <th
                      key={header.id}
                      className={header.column.columnDef.meta?.className || ''}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                    </th>
                  );
                })}
              </tr>
            ))}
        </tfoot>
      </table>
    </div>
  );
}
